import Vue from "vue";
import Vuex from "vuex";
import NotificationResource from "@/api/notifications";

Vue.use(Vuex);

const notificationResource = new NotificationResource();

const state = {
  notifications: [],
  loading: false,
  userSettings: {
    notification: false
  },
};

const getters = {
  notifications: (state) => state.notifications,
  unreadCount: (state) => state.notifications.filter((n) => !n.is_read).length,
  unreadNotifications: (state) => state.notifications.filter((n) => !n.is_read),
  readNotifications: (state) => state.notifications.filter((n) => n.is_read),
  userSettings: (state) => state.userSettings,
  isNotificationEnabled: (state) => state.userSettings.notification,
};

const mutations = {
  SET_NOTIFICATIONS(state, notifications) {
    state.notifications = notifications;
  },

  ADD_NOTIFICATION(state, notification) {
    // Check if notification already exists to avoid duplicates
    const exists = state.notifications.find((n) => n.id === notification.id);
    if (!exists) {
      state.notifications.unshift(notification);

      // Keep only the latest 50 notifications
      if (state.notifications.length > 50) {
        state.notifications = state.notifications.slice(0, 50);
      }
    }
  },

  MARK_AS_READ(state, notificationId) {
    const notification = state.notifications.find(
      (n) => n.id === notificationId
    );
    if (notification) {
      notification.is_read = true;
    }
  },

  MARK_ALL_AS_READ(state) {
    state.notifications.forEach((notification) => {
      if (!notification.is_read) {
        notification.is_read = true;
      }
    });
  },

  REMOVE_NOTIFICATION(state, notificationId) {
    const index = state.notifications.findIndex((n) => n.id === notificationId);
    if (index > -1) {
      state.notifications.splice(index, 1);
    }
  },

  CLEAR_ALL_NOTIFICATIONS(state) {
    state.notifications = [];
  },

  SET_LOADING(state, loading) {
    state.loading = loading;
  },

  SET_USER_SETTINGS(state, settings) {
    state.userSettings = settings;
  },

  UPDATE_NOTIFICATION_SETTING(state, enabled) {
    state.userSettings.notification = enabled;
  },
};

const actions = {
  async loadNotifications({ commit }) {
    commit("SET_LOADING", true);

    try {
      // Load notifications from backend
      const response = await notificationResource.getUserNotifications();
      if (response && response.data) {
        commit("SET_NOTIFICATIONS", response.data);
      }
      console.log("response", response);
      
    } catch (error) {
      console.error("Error loading notifications from backend:", error);

      // Fallback to localStorage if backend fails
      try {
        const savedNotifications = localStorage.getItem("notifications");
        if (savedNotifications) {
          const notifications = JSON.parse(savedNotifications);
          commit("SET_NOTIFICATIONS", notifications);
        }
      } catch (localError) {
        console.error(
          "Error loading notifications from localStorage:",
          localError
        );
      }
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async addNotification({ commit }, notification) {
    const newNotification = {
      id: notification.id || Date.now(),
      message: notification.message,
      type: notification.type || "info",
      url: notification.url || null,
      is_read: notification.is_read || false,
      factory_id: notification.factory_id || null,
      role_id: notification.role_id || null,
      created_at: notification.createdAt || new Date().toISOString(),
    };

    // Add to local state immediately for better UX
    commit("ADD_NOTIFICATION", newNotification);
  },

  async markAsRead({ commit }, notification) {
    commit("MARK_AS_READ", notification.id);

    try {
      // Update backend
      await notificationResource.markAsRead(notification.id);
    } catch (error) {
      console.error("Error marking notification as read in backend:", error);
    }
  },

  async markAllAsRead({ commit }) {
    commit("MARK_ALL_AS_READ");

    try {
      // Update backend
      await notificationResource.markAllAsRead();
    } catch (error) {
      console.error(
        "Error marking all notifications as read in backend:",
        error
      );
    }
  },

  async removeNotification({ commit }, notificationId) {
    commit("REMOVE_NOTIFICATION", notificationId);

    try {
      // Update backend
      await notificationResource.deleteNotification(notificationId);
    } catch (error) {
      console.error("Error removing notification from backend:", error);
    }
  },

  async clearAllNotifications({ commit }) {
    commit("CLEAR_ALL_NOTIFICATIONS");

    try {
      // Update backend
      await notificationResource.clearAll();
    } catch (error) {
      console.error("Error clearing notifications from backend:", error);
    }
  },

  // Additional actions for backend integration
  async getUnreadCount() {
    try {
      const response = await notificationResource.getUnreadCount();
      return response.count || 0;
    } catch (error) {
      console.error("Error getting unread count from backend:", error);
      return 0;
    }
  },

  async sendNotificationToUsers(_, { message, type = "info", userIds, title, factory_id, role_id }) {
    try {
      return await notificationResource.sendToUsers({
        message,
        type,
        user_ids: userIds,
        title,
        factory_id,
        role_id,
      });
    } catch (error) {
      console.error("Error sending notification to users:", error);
      throw error;
    }
  },

  async sendNotificationToAll(_, { message, type = "info", title, factory_id, role_id }) {
    try {
      return await notificationResource.sendToAll({
        message,
        type,
        title,
        factory_id,
        role_id,
      });
    } catch (error) {
      console.error("Error sending notification to all users:", error);
      throw error;
    }
  },

  // User settings actions
  async loadUserSettings({ commit }) {
    try {
      const response = await notificationResource.getUserSettings();
      if (!response) return;
  
      const raw = response.notification;
      const enabled =
        raw === true || raw === 1 || raw === '1' || raw === 'true';
  
      commit('SET_USER_SETTINGS', {
        notification: enabled,
      });
    } catch (error) {
      console.error('Error loading user settings:', error);
    }
  },

  async updateNotificationSetting({ commit }, enabled) {
    try {
      await notificationResource.updateUserNotificationSetting({
        notification: enabled.toString()
      });
      commit('UPDATE_NOTIFICATION_SETTING', enabled);
    } catch (error) {
      console.error("Error updating notification setting:", error);
      throw error;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
